import React from "react";
import _ from "lodash";
import moment from "moment";
import submit from "submit-form";
import DateInput from "../../../parts/inputs/date-input";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import ShareModal from "../../../parts/modals/share";
import getDayRangeString from "../../../utils/day-range-string";
import MetricsBackButton from "../../athlete-portal/metrics/metrics-back-button";
import set from "@rill/set";
import NavigationComponent from "../../athlete-portal/calendar/components/NavigationComponent";
import { ReportsBackHandler } from "../../athlete-portal/calendar/components/ReportsBackHandler";
import SvgIcon from "../../../parts/SvgIcon";

const {
  PRIMARIES,
  DATE_FORMATS: { DATE_PICKER_FORMAT, DATE_DAY_FORMAT },
} = require("../../../constants");

function View(props, { locals }) {
  const { query, search, feBranding = {} } = locals;
  const { units, mobileWV } = query;
  const exercisesRecordsLoad =
    query.type === "total"
      ? locals.exercisesRecordsLoad
      : _.pick(locals.exercisesRecordsLoad, [query.type]);

  return (
    <main
      role="reports-wrapper"
      className="Main Metrics"
      data-theme={mobileWV ? feBranding.appTheme : undefined}
      style={
        mobileWV
          ? {
              backgroundColor: "var(--background-color)",
              maxHeight: "unset",
              height: "100vh",
            }
          : null
      }
    >
      <NavigationComponent
        query={query}
        pathName={"/dashboard/metrics"}
        text="Calendar Day View"
        classNames="mobile-hidden"
      />
      <article role="report-content" id="strength-metrics">
        <ReportsBackHandler
          title={"Strength Metrics"}
          locals={locals}
          customBackUrl={`/dashboard/metrics/${search}${
            mobileWV ? "&&randomReload=true" : ""
          }`}
        />
        <header>
          <hgroup className="metrics-title">
            <MetricsBackButton
              url={`/dashboard/metrics/${search}`}
              classNames="mobile-hidden"
            />
            <SvgIcon
              src="/img/new-icons/personal-record-ico.svg"
              className="metrics-icon mobile-hidden"
            />
            <div>
              <SvgIcon
                src="/img/new-icons/personal-record-ico.svg"
                className="metrics-icon only-show-mobile"
              />
              <h3 className="record-table alt">Personal Record Table</h3>
              <ToggleLinksInput
                name="units"
                options={[
                  { text: "Kg", value: "metric" },
                  { text: "Lbs", value: "imperial" },
                ]}
                className={"only-show-mobile"}
              />
              <p className="p4 mobile-hidden">
                Easily identify lifetime or custom range training personal
                records for all exercises and rep ranges.
              </p>
            </div>
            <p className="p4 only-show-mobile">
              Easily identify lifetime or custom range training personal records
              for all exercises and rep ranges.
            </p>
          </hgroup>
          <hgroup className="metrics-dates">
            <div role="calendar-date-selectors">
              <DateInput
                name="start"
                placeholder="Start Date"
                value={query.start}
                max={query.end}
                onChange={(date) => handleDayChange(date, "start")}
              />
              <span>to</span>
              <DateInput
                name="end"
                placeholder="End Date"
                value={query.end}
                min={query.start}
                onChange={(date) => handleDayChange(date, "end")}
                alignRight={true}
              />
            </div>
          </hgroup>
          <hgroup className="metrics-data-options">
            <ToggleLinksInput
              name="units"
              options={[
                { text: "Kg", value: "metric" },
                { text: "Lbs", value: "imperial" },
              ]}
              className={"mobile-hidden"}
            />

            <ToggleLinksInput
              className="tabs"
              name="type"
              options={[
                { text: "Total", value: "total" },
                { text: "Squat", value: "squat" },
                { text: "Bench", value: "bench" },
                { text: "Deadlift", value: "deadlift" },
              ]}
            />
          </hgroup>
        </header>

        <section role="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>
                  <span className="left">Exercise:</span>
                  <span className="right">Reps:</span>
                </th>
                <th>1</th>
                <th>2</th>
                <th>3</th>
                <th>4</th>
                <th>5</th>
                <th>6</th>
                <th>7</th>
                <th>8</th>
                <th>9</th>
                <th>10</th>
                <th>11</th>
                <th>12</th>
              </tr>
            </thead>
            <tbody>
              {_.flatMap(PRIMARIES, (primary) => {
                const secondaries = exercisesRecordsLoad[primary];
                return _(secondaries)
                  .map((reps, secondary) => {
                    return { reps, secondary };
                  })
                  .sortBy("secondary")
                  .map(({ reps, secondary }) => {
                    const secondaryLabel = secondary ? `(${secondary})` : "";
                    return (
                      <tr key={primary + secondary}>
                        <td>
                          {_.capitalize(primary)} {secondaryLabel}
                        </td>
                        {_.map(_.range(1, 13), (i) => {
                          const load = _.get(reps[i], "load");
                          const date = moment.utc(_.get(reps[i], "date"));
                          if (!load) return <td key={i} />;
                          return (
                            <td key={i}>
                              <a
                                href={`/dashboard/calendar/?view=day&date=${date.format(
                                  DATE_DAY_FORMAT
                                )}`}
                                className="metrics-table-link"
                              >
                                {_.round(
                                  locals.fromMetric(load, units, false),
                                  1
                                )}
                              </a>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                  .value();
              })}
            </tbody>
          </table>
        </section>
      </article>
    </main>
  );
}

function handleDayChange(date, field) {
  submit("/actions/refresh", {
    method: "GET",
    body: { [field]: date },
  });
}

const RecordTable = (app) => {
  app.get(
    "/record-table",
    set("heading", "Personal Record Table"),
    async ({ req, res, load, locals, api, fail }) => {
      const { query } = req;
      const user = locals.activeUser._id;
      _.defaults(query, {
        type: "total",
        units: locals.units,
        start: moment.utc().startOf("month").format(DATE_PICKER_FORMAT),
        end: moment.utc().endOf("month").format(DATE_PICKER_FORMAT),
      });

      await load("exercisesRecordsLoad", {
        start: moment
          .utc(query.start, DATE_PICKER_FORMAT)
          .startOf("day")
          .toDate(),
        end: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
      });

      const modal = _.get(query, "modal", {});

      if (modal.type === "share" && !modal.snapshot) {
        // create share link
        try {
          modal.snapshot = (
            await (
              await api("snapshot", {
                method: "POST",
                body: {
                  type: "record-table",
                  data: JSON.stringify(
                    _.chain(locals)
                      .pick(["exercisesRecordsLoad"])
                      .assign({ start: query.start, end: query.end })
                      .value()
                  ),
                },
              })
            ).json()
          )._id;

          modal.date = getDayRangeString([
            moment.utc(query.start, DATE_PICKER_FORMAT),
            moment.utc(query.end, DATE_PICKER_FORMAT),
          ]);
          return res.refreshWith({ modal });
        } catch (e) {
          console.error(e.stack || e);
          fail(400, "Error creating share link.");
        }
      }
      const { activeUser } = locals;
      if (activeUser.subscription || activeUser.coach) {
        await load("feBranding", {});
      }
      locals.modal = {
        share: <ShareModal />,
      }[modal.type];

      res.body = <View />;
    }
  );
};

export default RecordTable;
